import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import Sidebar from './Sidebar';
import UserMenuPopup from './common/UserMenuPopup';
import ConfirmationDialog from './common/ConfirmationDialog';
import { colors } from '../constants';
import { useMainLayoutState } from '../hooks/pages/mainLayout/useMainLayoutState';
import { useMainLayoutSidebar } from '../hooks/pages/mainLayout/useMainLayoutSidebar';
import { useMainLayoutSearch } from '../hooks/pages/mainLayout/useMainLayoutSearch';
import { useMainLayoutLogout } from '../hooks/pages/mainLayout/useMainLayoutLogout';
import { getPageTitle, getPageIcon, isPowerBIPage } from '../utils/mainLayoutUtils';
import PageHeader from './mainLayout/PageHeader';
import SearchDialog from './mainLayout/SearchDialog';

const MainLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  // Initialize all hooks
  const layoutState = useMainLayoutState();
  
  useMainLayoutSidebar(layoutState.isSidebarCollapsed, layoutState.setIsSidebarCollapsed);

  const searchHandlers = useMainLayoutSearch(
    layoutState.searchDialogOpen,
    layoutState.searchQuery,
    layoutState.setSearchDialogOpen,
    layoutState.setSearchQuery
  );

  const { handleLogout } = useMainLayoutLogout(logout);

  // Handlers
  const handleMenuClick = (event) => {
    layoutState.setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    layoutState.setAnchorEl(null);
  };

  const handleSettingsClick = () => {
    // Navigate will be handled by UserMenuPopup
  };

  const handleLogoutClick = () => {
    layoutState.setLogoutDialogOpen(true);
  };

  const toggleSidebar = () => {
    layoutState.setIsSidebarCollapsed(!layoutState.isSidebarCollapsed);
  };

  const powerBIPage = isPowerBIPage(location);
  const pageTitle = getPageTitle(location);
  const pageIcon = getPageIcon(location);

  return (
    <Box sx={{ display: 'flex', height: '100vh', backgroundColor: colors.background }}>
      {/* Sidebar */}
      <Sidebar isCollapsed={layoutState.isSidebarCollapsed} onToggle={toggleSidebar} />

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'margin-left 0.3s ease',
        }}
      >
        {/* Top Header - Hide on Power BI Dashboard */}
        {!powerBIPage && (
          <PageHeader
            pageTitle={pageTitle}
            pageIcon={pageIcon}
            user={user}
            anchorEl={layoutState.anchorEl}
            onMenuClick={handleMenuClick}
          />
        )}

        {/* User Menu Popup */}
        <UserMenuPopup
          anchorEl={layoutState.anchorEl}
          open={Boolean(layoutState.anchorEl)}
          onClose={handleMenuClose}
          user={user}
          onSettingsClick={handleSettingsClick}
          onLogoutClick={handleLogoutClick}
        />

        {/* Logout Confirmation Dialog */}
        <ConfirmationDialog
          open={layoutState.logoutDialogOpen}
          onClose={() => layoutState.setLogoutDialogOpen(false)}
          onConfirm={handleLogout}
          title="Confirm Logout"
          message="Are you sure you want to logout? You will need to login again to access your account."
          confirmText="Logout"
          cancelText="Cancel"
          variant="danger"
        />

        {/* Search Dialog */}
        <SearchDialog
          open={layoutState.searchDialogOpen}
          onClose={searchHandlers.handleSearchClose}
          searchQuery={layoutState.searchQuery}
          onSearchChange={searchHandlers.handleSearchChange}
          onSearchSubmit={searchHandlers.handleSearchSubmit}
          onQuickSearch={searchHandlers.handleQuickSearch}
        />

        {/* Page Content Area */}
        <Box
          data-content-area
          sx={{
            flexGrow: 1,
            overflow: powerBIPage ? 'hidden' : 'auto',
            backgroundColor: colors.background,
            position: 'relative',
            margin: 0,
            padding: 0,
            width: '100%',
            height: powerBIPage ? '100%' : 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
